import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Chip,
  InputAdornment,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  DataGrid,
} from "components/mui";
import MenuItem from "@mui/material/MenuItem";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HistoryIcon from "@mui/icons-material/History";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import { ROUTE_EMAIL_SENDER } from "constants/routerPath";
import { useNotificationProvider } from "hooks";
import {
  GetEmailTemplates,
  DuplicateTemplate,
  deleteEmailTemplate,
} from "api/canmail/CANmailAPI";
import TemplatePreview from "../templatePreview/TemplatePreview";
import TemplateDetailsModal from "./TemplateDetailsModal";
import TemplateVersionHistoryModal from "./TemplateVersionHistoryModal";

const ALL_FOLDERS = "__all__";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return format(parseISO(iso), "PP");
  } catch {
    return "—";
  }
};

const editorLabel = (t) => {
  const type = t.editor_type || (t.template_content_drop ? "drop" : "text");
  return type === "drop" ? "Visual" : "Text";
};

/**
 * Template Manager — the CANmail template library.
 *
 * A searchable/sortable grid of every template with folder grouping, a live
 * preview pane, and per-row actions: preview, load into the Email Sender,
 * edit details, duplicate, version history/restore, and delete.
 */
function TemplateManager() {
  const navigate = useNavigate();
  const { notifySuccess, notifyError } = useNotificationProvider();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [folderFilter, setFolderFilter] = useState(ALL_FOLDERS);
  const [selectedId, setSelectedId] = useState(null);
  const [pageSize, setPageSize] = useState(25);

  const [detailsModal, setDetailsModal] = useState({ open: false, template: null });
  const [versionModal, setVersionModal] = useState({ open: false, template: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, template: null });
  const [duplicateModal, setDuplicateModal] = useState({ open: false, template: null, name: "" });
  const [actionBusy, setActionBusy] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await GetEmailTemplates();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("[TemplateManager] Error loading templates:", err);
      setError("Could not load templates. Please try again.");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const folders = useMemo(() => {
    const set = new Set();
    templates.forEach((t) => {
      if (t.folder && t.folder.trim()) set.add(t.folder.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return templates.filter((t) => {
      if (folderFilter !== ALL_FOLDERS) {
        const f = (t.folder || "").trim();
        if (folderFilter === "" ? f !== "" : f !== folderFilter) return false;
      }
      if (!term) return true;
      const haystack = [t.template_name, t.description, t.folder, t.tags]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [templates, searchTerm, folderFilter]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.template_id === selectedId) || null,
    [templates, selectedId]
  );

  const handleLoad = (template) => {
    navigate(`${ROUTE_EMAIL_SENDER}?templateId=${template.template_id}`);
  };

  const handleConfirmDelete = async () => {
    const template = deleteModal.template;
    if (!template) return;
    setActionBusy(true);
    try {
      await deleteEmailTemplate(template.template_id);
      notifySuccess(`Template "${template.template_name}" deleted.`);
      if (selectedId === template.template_id) setSelectedId(null);
      setDeleteModal({ open: false, template: null });
      fetchTemplates();
    } catch (err) {
      console.error("[TemplateManager] Error deleting template:", err);
      notifyError("Could not delete the template. Please try again.");
    } finally {
      setActionBusy(false);
    }
  };

  const handleConfirmDuplicate = async () => {
    const { template, name } = duplicateModal;
    if (!template) return;
    setActionBusy(true);
    try {
      await DuplicateTemplate(template.template_id, name.trim() || undefined);
      notifySuccess("Template duplicated.");
      setDuplicateModal({ open: false, template: null, name: "" });
      fetchTemplates();
    } catch (err) {
      console.error("[TemplateManager] Error duplicating template:", err);
      notifyError("Could not duplicate the template. Please try again.");
    } finally {
      setActionBusy(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "template_name",
        headerName: "Name",
        flex: 1.4,
        minWidth: 200,
        renderCell: (params) => (
          <Box sx={{ overflow: "hidden" }}>
            <Typography variant="body2" noWrap>
              {params.row.template_name}
            </Typography>
            {params.row.description && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {params.row.description}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: "editor_type",
        headerName: "Type",
        width: 100,
        renderCell: (params) => (
          <Chip
            label={editorLabel(params.row)}
            size="small"
            variant="outlined"
            color={editorLabel(params.row) === "Visual" ? "primary" : "default"}
          />
        ),
      },
      {
        field: "folder",
        headerName: "Folder",
        width: 150,
        valueGetter: (params) => params.row.folder || "—",
      },
      {
        field: "updated_at",
        headerName: "Updated",
        width: 130,
        type: "date",
        valueGetter: (params) => (params.row.updated_at ? new Date(params.row.updated_at) : null),
        renderCell: (params) => formatDate(params.row.updated_at),
      },
      {
        field: "updated_by_name",
        headerName: "Updated by",
        width: 150,
        valueGetter: (params) => params.row.updated_by_name || params.row.created_by_name || "—",
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 220,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box sx={{ display: "flex" }}>
            <Tooltip title="Load into Email Sender" arrow>
              <IconButton size="small" color="primary" onClick={() => handleLoad(params.row)}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit details" arrow>
              <IconButton size="small" onClick={() => setDetailsModal({ open: true, template: params.row })}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Duplicate" arrow>
              <IconButton
                size="small"
                onClick={() =>
                  setDuplicateModal({
                    open: true,
                    template: params.row,
                    name: `Copy of ${params.row.template_name}`,
                  })
                }
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Version history" arrow>
              <IconButton size="small" onClick={() => setVersionModal({ open: true, template: params.row })}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete" arrow>
              <IconButton
                size="small"
                sx={{ color: "error.main" }}
                onClick={() => setDeleteModal({ open: true, template: params.row })}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, m: { xs: 0, sm: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" component="h1">
            Template Manager
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {templates.length} template{templates.length === 1 ? "" : "s"}
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => navigate(ROUTE_EMAIL_SENDER)}>
          Open Email Sender
        </Button>
      </Box>

      {/* Control bar */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search name, description, folder or tags…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 280, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Folder"
          value={folderFilter}
          onChange={(e) => setFolderFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value={ALL_FOLDERS}>All folders</MenuItem>
          <MenuItem value="">
            <em>No folder</em>
          </MenuItem>
          {folders.map((f) => (
            <MenuItem key={f} value={f}>
              {f}
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title="Refresh" arrow>
          <span>
            <IconButton onClick={fetchTemplates} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Grid of templates */}
        <Grid item xs={12} md={7}>
          <Box sx={{ height: 560, width: "100%" }}>
            <DataGrid
              rows={filteredTemplates}
              columns={columns}
              getRowId={(row) => row.template_id}
              loading={loading}
              onRowClick={(params) => setSelectedId(params.row.template_id)}
              pageSize={pageSize}
              onPageSizeChange={(newSize) => setPageSize(newSize)}
              rowsPerPageOptions={[10, 25, 50, 100]}
              disableSelectionOnClick
              density="standard"
              sx={{
                "& .MuiDataGrid-row": { cursor: "pointer" },
              }}
            />
          </Box>
        </Grid>

        {/* Preview pane */}
        <Grid item xs={12} md={5}>
          <Box
            sx={{
              height: 560,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <Typography variant="subtitle2" noWrap>
                {selectedTemplate ? selectedTemplate.template_name : "Preview"}
              </Typography>
              {selectedTemplate && (
                <Button size="small" startIcon={<OpenInNewIcon fontSize="small" />} onClick={() => handleLoad(selectedTemplate)}>
                  Load
                </Button>
              )}
            </Box>
            <Box sx={{ flex: 1, overflow: "auto" }}>
              {selectedTemplate ? (
                <TemplatePreview template={selectedTemplate} />
              ) : (
                <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Select a template to preview it here.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Edit details */}
      <TemplateDetailsModal
        open={detailsModal.open}
        template={detailsModal.template}
        folders={folders}
        onClose={() => setDetailsModal({ open: false, template: null })}
        onSaved={fetchTemplates}
      />

      {/* Version history + restore */}
      <TemplateVersionHistoryModal
        open={versionModal.open}
        template={versionModal.template}
        onClose={() => setVersionModal({ open: false, template: null })}
        onRestored={fetchTemplates}
      />

      {/* Duplicate */}
      <Dialog
        open={duplicateModal.open}
        onClose={actionBusy ? undefined : () => setDuplicateModal({ open: false, template: null, name: "" })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Duplicate Template</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            label="Name for the copy"
            fullWidth
            value={duplicateModal.name}
            onChange={(e) => setDuplicateModal((prev) => ({ ...prev, name: e.target.value }))}
            inputProps={{ maxLength: 255 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDuplicateModal({ open: false, template: null, name: "" })} disabled={actionBusy}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDuplicate} variant="contained" disabled={actionBusy}>
            {actionBusy ? "Duplicating…" : "Duplicate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={deleteModal.open}
        onClose={actionBusy ? undefined : () => setDeleteModal({ open: false, template: null })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body1">
            Are you sure you want to delete <strong>{deleteModal.template?.template_name}</strong>?
          </Typography>
          <Alert severity="warning">
            This permanently deletes the template and its version history. This cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteModal({ open: false, template: null })} disabled={actionBusy}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error" disabled={actionBusy}>
            {actionBusy ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {loading && templates.length === 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <CircularProgress />
        </Box>
      )}
    </Paper>
  );
}

export default TemplateManager;
